import React from "react";
import { Play, Pause, RefreshCw, ArrowLeft, ArrowRight, Eye, EyeOff, LayoutGrid, LayoutList } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, TooltipProps } from "recharts";
import { formatTime, formatAxisTime } from "../../utils/sectorDataUtils";
import { useSectorMomentum } from "../../hooks/useSectorMomentum";
import { useIsMobile } from "../../hooks/use-mobile";
import { Button } from "../../components/ui/button";
import { Toggle } from "../../components/ui/toggle";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";
import { Subhai } from "..";

// Custom tooltip to show only top 2 and bottom 2 performers
const CustomTooltip = ({ active, payload, label, topPerformers, bottomPerformers }:
  TooltipProps<any, any> & {
    topPerformers: Array<{ id: string, name: string, color: string, change: number }>,
    bottomPerformers: Array<{ id: string, name: string, color: string, change: number }>
  }) => {

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0D0D0D] border border-white/20 p-2 rounded-md shadow-lg text-xs">
        <p className="font-bold text-white mb-1">{formatTime(label as string)}</p>
        <div className="space-y-1">
          <p className="text-green-400 font-semibold">Top Performers:</p>
          {topPerformers.map((sector) => {
            const matching = payload?.find(p => p.name === sector.name);
            const value = typeof matching?.value === 'number' ? matching.value.toFixed(2) : 'N/A';

            return (
              <div key={sector.id} className="flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: sector.color }}
                ></span>
                <span className="text-white/90">{sector.name}:</span>
                <span className="text-green-300">{value}%</span>
              </div>
            );
          })}
          <div className="my-1 border-t border-white/10"></div>
          <p className="text-red-400 font-semibold">Bottom Performers:</p>
          {bottomPerformers.map((sector) => {
            const matching = payload?.find(p => p.name === sector.name);
            const value = typeof matching?.value === 'number' ? matching.value.toFixed(2) : 'N/A';

            return (
              <div key={sector.id} className="flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: sector.color }}
                ></span>
                <span className="text-white/90">{sector.name}:</span>
                <span className="text-red-300">{value}%</span>
              </div>
            );
          })}

        </div>
      </div>
    );
  }
  return null;
};

const LiveSectorMomentum: React.FC = () => {
  const {
    liveSectorData,
    timePoints,
    currentTimeIndex,
    setCurrentTimeIndex,
    isPlaying,
    refreshing,
    processedData,
    sectorRanks,
    topAndBottomPerformers,
    visibleSectors,
    showAllSectors,
    handleRefresh,
    togglePlayback,
    toggleSectorVisibility,
    toggleAllSectors,
    yDomain
  } = useSectorMomentum();

  const isMobile = useIsMobile();

  // Add state to track which sector line is being hovered
  const [hoveredSector, setHoveredSector] = React.useState<string | null>(null);
  const [displayMode, setDisplayMode] = React.useState<'list' | 'grid'>('grid');

  // Get the mid point to split the sectors into two columns
  const midPoint = Math.ceil(sectorRanks.length / 2);

  return (
    <div className={cn(
      "bg-black/40 rounded-lg border border-white/10",
      isMobile ? "p-3" : "p-4"
    )}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Live Sector Momentum</h3>
        <div className="flex gap-2">
          <Toggle
            pressed={displayMode === 'grid'}
            onPressedChange={() => setDisplayMode(displayMode === 'grid' ? 'list' : 'grid')}
            className="bg-black/20 hover:bg-white/5 text-white"
            aria-label="Toggle display mode"
          >
            {displayMode === 'grid' ?
              <LayoutGrid className="h-4 w-4" /> :
              <LayoutList className="h-4 w-4" />
            }
          </Toggle>
          <Button
            onClick={toggleAllSectors}
            variant="outline"
            size="sm"
            className="bg-black/20 hover:bg-white/5 text-white"
            title={showAllSectors ? "Show Top/Bottom Performers" : "Show All Sectors"}
          >
            {showAllSectors ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="bg-black/20 hover:bg-white/5 text-white"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="text-sm text-white/60 mb-4">
        Track which sectors are gaining strength or fading across the day.
      </div>

      {/* Sectors Legend */}
      <div className="relative">
        <Subhai />

        <div className="mb-6 bg-black/40 rounded-lg p-4">

          {displayMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                {sectorRanks.slice(0, midPoint).map((sector, index) => (
                  <SectorItem
                    key={sector.id}
                    sector={sector}
                    index={index}
                    isVisible={visibleSectors.includes(sector.id)}
                    isHovered={hoveredSector === sector.id}
                    totalCount={sectorRanks.length}
                    onToggleVisibility={() => toggleSectorVisibility(sector.id)}
                    onHover={setHoveredSector}
                  />
                ))}
              </div>
              <div className="space-y-2 md:border-l md:border-white/10 md:pl-4">
                {sectorRanks.slice(midPoint).map((sector, index) => (
                  <SectorItem
                    key={sector.id}
                    sector={sector}
                    index={index + midPoint}
                    isVisible={visibleSectors.includes(sector.id)}
                    isHovered={hoveredSector === sector.id}
                    totalCount={sectorRanks.length}
                    onToggleVisibility={() => toggleSectorVisibility(sector.id)}
                    onHover={setHoveredSector}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sectorRanks.map((sector, index) => (
                <SectorItem
                  key={sector.id}
                  sector={sector}
                  index={index}
                  isVisible={visibleSectors.includes(sector.id)}
                  isHovered={hoveredSector === sector.id}
                  totalCount={sectorRanks.length}
                  onToggleVisibility={() => toggleSectorVisibility(sector.id)}
                  onHover={setHoveredSector}
                  horizontal={true}
                />
              ))}
            </div>
          )}
        </div>

        <div className={cn(
          "w-full bg-black/40 rounded-lg p-4",
          isMobile ? "h-[350px]" : "h-[500px]"
        )}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={processedData}
              margin={isMobile ?
                { top: 10, right: 10, left: 0, bottom: 10 } :
                { top: 20, right: 30, left: 20, bottom: 10 }}
              onMouseLeave={() => setHoveredSector(null)}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="time"
                tick={{ fill: "rgba(255,255,255,0.6)", fontSize: isMobile ? 8 : 10 }}
                stroke="rgba(255,255,255,0.2)"
                tickFormatter={(value, index) => formatAxisTime(value, index, timePoints.length)}
                interval={isMobile ? 'preserveEnd' : 0}
                minTickGap={isMobile ? 60 : 30}
              />
              <YAxis
                domain={yDomain}
                tick={{ fill: "rgba(255,255,255,0.6)", fontSize: isMobile ? 8 : 10 }}
                stroke="rgba(255,255,255,0.2)"
                width={isMobile ? 30 : 40}
                label={isMobile ? {
                  value: "% Change",
                  angle: -90,
                  position: "insideLeft",
                  offset: -5,
                  style: { textAnchor: "middle", fill: "rgba(255,255,255,0.6)", fontSize: 8 }
                } : {
                  value: "% Change from Open",
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fill: "rgba(255,255,255,0.6)", fontSize: 12 }
                }}
              />
              <Tooltip
                content={({ active, payload, label }) => (
                  <CustomTooltip
                    active={active}
                    payload={payload}
                    label={label}
                    topPerformers={topAndBottomPerformers.top}
                    bottomPerformers={topAndBottomPerformers.bottom}
                  />
                )}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
              {sectorRanks
                .filter(sector => visibleSectors.includes(sector.id))
                .map((sector, index) => {
                  // Use rank in sectorRanks to determine styling, not the filtered index
                  const rankIndex = sectorRanks.findIndex(s => s.id === sector.id);
                  const isHovered = hoveredSector === sector.id;
                  const isTopPerformer = rankIndex === 0;
                  const isBottomPerformer = rankIndex === sectorRanks.length - 1;

                  // Reduced stroke width values
                  const strokeWidth = isHovered ? 3 :
                    isTopPerformer ? 2.5 :
                      isBottomPerformer ? 2.5 :
                        rankIndex === 1 ? 2 :
                          rankIndex === 2 ? 2 :
                            rankIndex === sectorRanks.length - 2 ? 2 :
                              rankIndex === sectorRanks.length - 3 ? 1.8 :
                                rankIndex < 5 ? 1.5 : 1;

                  const strokeOpacity = isHovered ? 1 :
                    isTopPerformer || isBottomPerformer ? 0.95 :
                      rankIndex === 1 || rankIndex === sectorRanks.length - 2 ? 0.9 :
                        rankIndex === 2 || rankIndex === sectorRanks.length - 3 ? 0.85 :
                          rankIndex < 5 ? 0.8 : 0.7;

                  // Add glow effect for top and bottom performers
                  const filter = (isTopPerformer || isBottomPerformer || isHovered) ?
                    "url(#glow)" : undefined;

                  return (
                    <Line
                      key={sector.id}
                      type="monotoneX"
                      dataKey={sector.id}
                      name={sector.name}
                      stroke={sector.color}
                      strokeWidth={isMobile ? Math.max(0.8, strokeWidth * 0.7) : strokeWidth}
                      strokeOpacity={strokeOpacity}
                      dot={false}
                      activeDot={(isTopPerformer || isBottomPerformer || isHovered) ?
                        { r: isMobile ? 4 : 5, strokeWidth: 1, fill: sector.color, stroke: "#ffffff", filter } :
                        { r: isMobile ? 2 : 3 }}
                      animationDuration={300}
                      onMouseOver={() => setHoveredSector(sector.id)}
                      className="transition-all duration-300"
                    />
                  );
                })}
              <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="pulse" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
          <div className="text-center text-xs sm:text-sm text-white/60">
            Current Time: {formatTime(timePoints[currentTimeIndex])}
          </div>
          <div className="flex justify-center items-center gap-2 sm:gap-4">
            <button
              onClick={() => setCurrentTimeIndex(prev => Math.max(0, prev - 1))}
              disabled={currentTimeIndex === 0}
              className="p-1.5 sm:p-2 rounded-lg bg-black/20 hover:bg-white/5 disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <input
              type="range"
              min={0}
              max={timePoints.length - 1}
              value={currentTimeIndex}
              onChange={(e) => setCurrentTimeIndex(Number(e.target.value))}
              className="w-full accent-white/40"
            />
            <button
              onClick={() => setCurrentTimeIndex(prev => Math.min(timePoints.length - 1, prev + 1))}
              disabled={currentTimeIndex === timePoints.length - 1}
              className="p-1.5 sm:p-2 rounded-lg bg-black/20 hover:bg-white/5 disabled:opacity-30"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex justify-center">
            <button
              onClick={togglePlayback}
              className="px-4 sm:px-6 py-2 rounded-lg bg-black/20 hover:bg-white/5 transition-all text-sm"
            >
              {isPlaying ? (<><Pause className="inline h-4 w-4 mr-2" />Pause</>) : (
                <>{currentTimeIndex === timePoints.length - 1 ? (
                  <><Play className="inline h-4 w-4 mr-2" />Replay</>
                ) : (
                  <><Play className="inline h-4 w-4 mr-2" />Play</>
                )}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Extract sector item into a separate component
interface SectorItemProps {
  sector: {
    id: string;
    name: string;
    color: string;
    change: number;
  };
  index: number;
  isVisible: boolean;
  isHovered: boolean;
  totalCount: number;
  onToggleVisibility: () => void;
  onHover: (id: string | null) => void;
  horizontal?: boolean;
}

const SectorItem: React.FC<SectorItemProps> = ({
  sector,
  index,
  isVisible,
  isHovered,
  totalCount,
  onToggleVisibility,
  onHover,
  horizontal = false
}) => {
  return (
    <div
      className={`
        ${isVisible ? (index === 0 ? 'font-bold text-white' : 'text-white/80') : 'text-white/30'} 
        cursor-pointer transition-all duration-200 hover:bg-white/10 px-1 py-0.5 rounded truncate flex items-center
        ${horizontal ? 'inline-flex mr-1' : 'block'}
      `}
      onClick={onToggleVisibility}
      onMouseEnter={() => onHover(sector.id)}
      onMouseLeave={() => onHover(null)}
      title={`${sector.name}: ${sector.change.toFixed(2)}%`}
    >
      <span
        className={`inline-block w-2 h-2 rounded-full mr-1 ${isHovered ? 'animate-pulse' : ''} ${!isVisible ? 'opacity-40' : ''
          }`}
        style={{ backgroundColor: sector.color }}
      ></span>
      <span className="truncate">{sector.name}</span>
      {index === 0 && isVisible && <span className="ml-1 text-green-400">▲</span>}
      {index === totalCount - 1 && isVisible && <span className="ml-1 text-red-400">▼</span>}
    </div>
  );
};

export default LiveSectorMomentum;

